import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiFileText } from 'react-icons/fi';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { LoadingButton } from './Loader';

const SECTIONS: Array<{ title: string; body: string; subsections?: Array<{ title: string; body: string }> }> = [
  {
    title: '1. Background',
    body: `TradePilot operates a copy-trading platform that allows members of the platform (“Passengers”) to automatically mirror the trades of verified traders in their own brokerage accounts. Pilots are the traders whose performance is displayed on TradePilot’s public leaderboard and whose trades passengers may choose to copy. This Agreement governs the relationship between TradePilot and each Pilot, including compensation, conduct, and the respective rights and obligations of both Parties.`,
  },
  {
    title: '2. Definitions',
    body: '',
    subsections: [
      {
        title: '2.1 Pilot',
        body: `“Pilot” means any trader approved by TradePilot to appear on the Company’s leaderboard and/or to have their trades copied by Passengers. A person who serves as Head of Trading Department or in another TradePilot leadership role is still a Pilot with respect to any personal trading account they operate on the platform. Leadership status does not replace or waive Pilot status.`,
      },
      {
        title: '2.2 Head of Trading Department',
        body: `Refers to Brice and Iftekhar, who oversee trading operations, platform integrity, and leaderboard governance for TradePilot, and who, together with other TradePilot leadership, hold final decision-making authority over matters this Agreement reserves to leadership, including live-broadcast approvals under Section 9.`,
      },
      {
        title: '2.3 Passenger or Member',
        body: `A TradePilot member who elects to copy a Pilot’s trades through the TradePilot platform.`,
      },
      {
        title: '2.4 Compensation Period',
        body: `The recurring monthly billing cycle (approximately thirty (30) days) used to calculate Pilot compensation, unless changed by TradePilot under Section 4.5.`,
      },
      {
        title: '2.5 Active Trading Day',
        body: `A day within a Compensation Period on which markets are open for trading (generally Monday through Friday, excluding recognized market holidays) and on which a Passenger’s account is actively configured to copy a given Pilot’s trades on the TradePilot platform. A Compensation Period contains approximately twenty (20) Active Trading Days, calculated uniformly for all Pilots regardless of the asset class traded (forex, options, equities, or otherwise). Weekends and non-trading days do not accrue compensation for any Pilot, even where a Passenger remains configured to copy that Pilot over a weekend.`,
      },
      {
        title: '2.6 Leaderboard',
        body: `TradePilot’s public ranking and discovery feature that displays Pilots and their performance metrics to Passengers.`,
      },
      {
        title: '2.7 Private Community Mode',
        body: `The white-labeled configuration described in Section 5.`,
      },
    ],
  },
  {
    title: '3. Pilot Role & Responsibilities',
    body: '',
    subsections: [
      {
        title: '3.1',
        body: `A Pilot’s sole operational obligation under this Agreement is to trade using a brokerage account compatible with TradePilot’s copy-trading software, consistent with the trading style or strategy represented on the Pilot’s Leaderboard profile.`,
      },
      {
        title: '3.2',
        body: `TradePilot does not require Pilots to create content, manage a community, or engage in any promotional activity as a condition of compensation. Pilots may elect to engage in such activity under Sections 7 and 8, but it is never mandatory.`,
      },
      {
        title: '3.3',
        body: `Pilots are responsible for maintaining a funded, active, compatible brokerage account and for the accuracy of any performance data reflected on their Leaderboard profile.`,
      },
    ],
  },
  {
    title: '4. Compensation',
    body: '',
    subsections: [
      {
        title: '4.1 Base Rate',
        body: `TradePilot will pay each Pilot $50.00 per Passenger per Compensation Period for each Passenger who actively copies that Pilot’s trades, subject to the proration in Section 4.2.`,
      },
      {
        title: '4.2 Daily Proration',
        body: `Compensation accrues per Active Trading Day: $50.00 divided by 20 Active Trading Days equals $2.50 per Active Trading Day, multiplied by the number of Active Trading Days a given Passenger accrues with that Pilot during the Compensation Period. This rate applies uniformly regardless of what the Pilot trades (forex, options, equities, or otherwise) and does not vary based on how many days per week a given market happens to be open. A Passenger who copies a Pilot for only part of a period generates compensation only for the Active Trading Days actually copied. For example, 10 Active Trading Days (roughly half of the 20 in a period) yields approximately $25.00, half of the full-period rate.`,
      },
      {
        title: '4.3 Mid-Period Switching',
        body: `If a Passenger stops copying one Pilot and starts copying another within the same or a following Compensation Period, each Pilot is compensated strictly for the Active Trading Days that Passenger accrued with them. No Pilot is owed compensation for days a Passenger spent copying a different Pilot, and no Pilot forfeits compensation properly earned for Active Trading Days accrued before a Passenger switches away.`,
      },
      {
        title: '4.4 Payment Schedule',
        body: `TradePilot will calculate and remit Pilot compensation monthly, in arrears, within five (5) business days following the close of each Compensation Period, via the payment method on file with TradePilot.`,
      },
      {
        title: '4.5 Right to Modify Compensation Terms',
        body: `TradePilot reserves the right to modify the per-Passenger compensation rate, the proration methodology, or the Compensation Period structure at its sole discretion, at any time, upon at least fifteen (15) days’ advance written notice to Pilots. Changes apply only to Active Trading Days accrued after the change’s effective date and never reduce compensation already earned.`,
      },
    ],
  },
  {
    title: '5. Private Community Mode',
    body: '',
    subsections: [
      {
        title: '5.1',
        body: `A Pilot may request to operate under Private Community Mode, a configuration of TradePilot’s software that lets the Pilot’s own audience or community copy the Pilot’s trades without visibility into TradePilot’s public Leaderboard or other Pilots.`,
      },
      {
        title: '5.2',
        body: `Passengers onboarded through Private Community Mode remain TradePilot members and are subject to TradePilot’s standard membership terms. This mode limits Leaderboard visibility only; it does not alter the compensation structure in Section 4 or any other term of this Agreement unless separately agreed in writing.`,
      },
      {
        title: '5.3',
        body: `Availability of Private Community Mode is subject to TradePilot’s technical capabilities and may be modified, limited, or discontinued by TradePilot at its discretion.`,
      },
    ],
  },
  {
    title: '6. Leaderboard Placement & Passenger Choice',
    body: '',
    subsections: [
      {
        title: '6.1',
        body: `Except where a Pilot has elected Private Community Mode, TradePilot will display the Pilot’s performance on its public Leaderboard, and Passengers may discover and elect to copy the Pilot based on that performance without any further action required from the Pilot.`,
      },
      {
        title: '6.2',
        body: `TradePilot operates as a transparent platform. Passengers are free to browse the Leaderboard and follow, switch away from, or stop following any Pilot at any time, for any reason. No Pilot acquires any ownership, exclusivity, or proprietary interest in any Passenger relationship, and TradePilot does not guarantee the number of Passengers who will elect to copy any given Pilot.`,
      },
    ],
  },
  {
    title: '7. Promotion & Representation',
    body: '',
    subsections: [
      {
        title: '7.1',
        body: `Pilots may publicly identify themselves as using or being featured on the TradePilot platform, and may state that their trades can be copied by others through TradePilot’s software.`,
      },
      {
        title: '7.2',
        body: `Pilots may not state or imply that they are an employee, staff member, officer, or agent of TradePilot AI Inc., and may not make any representation or incur any obligation on TradePilot’s behalf.`,
      },
      {
        title: '7.3',
        body: `Any Pilot promotional material referencing TradePilot must not state or imply guaranteed returns, guaranteed income, “risk-free” trading, or similar claims, and should carry risk disclosure consistent with Section 13.`,
      },
    ],
  },
  {
    title: '8. Live Broadcast Requests',
    body: '',
    subsections: [
      {
        title: '8.1',
        body: `A Pilot may request to broadcast a live trading session or livestream to TradePilot’s Passenger base through the platform, for example, to build trust or teach trading concepts to prospective followers.`,
      },
      {
        title: '8.2',
        body: `All such requests are subject to review and final approval by TradePilot leadership, including the Head of Trading Department (Brice and Iftekhar), who may approve, deny, or condition any broadcast request at their discretion.`,
      },
    ],
  },
  {
    title: '9. Independent Contractor Status',
    body: '',
    subsections: [
      {
        title: '9.1',
        body: `The Pilot is an independent contractor, not an employee, partner, joint venturer, or agent of TradePilot AI Inc. Nothing in this Agreement creates an employment, agency, or partnership relationship between the Parties.`,
      },
      {
        title: '9.2',
        body: `TradePilot will not withhold taxes from Pilot compensation. The Pilot is solely responsible for all applicable federal, state, and local taxes, and for any licenses or registrations required in connection with the Pilot’s trading activity.`,
      },
      {
        title: '9.3',
        body: `The Pilot is not entitled to employee benefits of any kind from TradePilot.`,
      },
    ],
  },
  {
    title: '10. Confidentiality',
    body: `The Pilot will keep confidential any non-public information about TradePilot’s software, Passenger data, business operations, or compensation structure obtained through this Agreement, and will use such information solely to perform as a Pilot on the platform.`,
  },
  {
    title: '11. Prohibited Conduct',
    body: `The following conduct by a Pilot is a material violation of this Agreement: publicly disparaging TradePilot, its platform, leadership, or brand; making disparaging or negative statements to Passengers, members, or other users of TradePilot’s software about the Company, its platform, or its leadership; soliciting, inducing, or attempting to move Passengers, members, or other users of TradePilot’s software to a competing platform or service (“poaching”); misrepresenting the Pilot’s relationship to TradePilot, including any claim of employment or agency status; engaging in fraudulent, manipulative, or misleading trading conduct intended to inflate Leaderboard ranking or performance metrics.`,
  },
  {
    title: '12. Remedies & Enforcement',
    body: '',
    subsections: [
      {
        title: '12.1',
        body: `If TradePilot determines, in its reasonable discretion, that a Pilot has engaged in conduct described in Section 11, TradePilot may take one or more of the following actions as it deems appropriate to the severity of the violation: withhold or forfeit compensation attributable to the affected Passenger(s); reassign or reallocate affected Passengers to another Pilot; suspend or remove the Pilot’s Leaderboard visibility; suspend or terminate this Agreement.`,
      },
      {
        title: '12.2',
        body: `These remedies are in addition to, and not in lieu of, any other legal or equitable remedy available to TradePilot.`,
      },
    ],
  },
  {
    title: '13. Risk Disclosures & Regulatory Acknowledgments',
    body: '',
    subsections: [
      {
        title: '13.1',
        body: `Forex trading and copy trading involve substantial risk of loss and are not suitable for all investors. Past performance does not guarantee future results. No Pilot, TradePilot, or other platform participant guarantees any level of return, income, or profit to any Passenger.`,
      },
      {
        title: '13.2',
        body: `The Pilot acknowledges that compensation under this Agreement is paid for making the Pilot’s trades available for copying, not for providing individualized investment advice, and that neither the Pilot nor TradePilot is thereby represented to act as a registered investment adviser, commodity trading advisor, or broker-dealer with respect to any Passenger.`,
      },
      {
        title: '13.3',
        body: `The Pilot will not use language in any promotional or public-facing material, including under Section 7, that states or implies guaranteed returns, guaranteed passive income, or risk-free trading.`,
      },
    ],
  },
  {
    title: '14. Indemnification & Limitation of Liability',
    body: '',
    subsections: [
      {
        title: '14.1',
        body: `Each Party will indemnify and hold the other harmless from third-party claims arising from that Party’s breach of this Agreement or violation of applicable law.`,
      },
      {
        title: '14.2',
        body: `To the maximum extent permitted by law, TradePilot’s aggregate liability to the Pilot under this Agreement is limited to the compensation paid to the Pilot in the three (3) months preceding the claim.`,
      },
      {
        title: '14.3 Technical & Platform Failures',
        body: `TradePilot’s software, cloud infrastructure, and third-party integrations (including brokerage connectivity) may from time to time experience outages, errors, delays, data loss, or other technical failures beyond TradePilot’s reasonable control. To the maximum extent permitted by law, the Pilot waives any claim against TradePilot for lost compensation, trading losses, or other damages arising from such technical or software failures, except for damages caused by TradePilot’s gross negligence or willful misconduct. The Pilot should report any such issue to TradePilot as soon as reasonably possible so it can be investigated and addressed.`,
      },
    ],
  },
  {
    title: '15. General Provisions',
    body: '',
    subsections: [
      {
        title: '15.1 Governing Law',
        body: `This Agreement is governed by the laws of the State of Florida, without regard to conflict-of-law principles.`,
      },
      {
        title: '15.2 Entire Agreement',
        body: `This Agreement, together with TradePilot’s platform Terms of Service, constitutes the entire agreement between the Parties on this subject matter.`,
      },
      {
        title: '15.3 Amendment',
        body: `Except as provided in Section 4.5, this Agreement may only be amended in writing signed by both Parties.`,
      },
      {
        title: '15.4 Assignment',
        body: `The Pilot may not assign this Agreement without TradePilot’s prior written consent. TradePilot may assign this Agreement in connection with a merger, acquisition, or sale of assets.`,
      },
      {
        title: '15.5 Notices',
        body: `Notices under this Agreement will be sent to the contact information on file for each Party.`,
      },
      {
        title: '15.6 Term & Termination',
        body: `This Agreement begins on the Effective Date and continues until terminated by either Party. Either Party may terminate at any time, with or without cause, upon fifteen (15) days’ written notice. TradePilot may terminate immediately upon a material violation described in Section 11. Upon termination, TradePilot will pay the Pilot any compensation properly earned for Active Trading Days accrued before the termination date, calculated under Section 4.`,
      },
    ],
  },
];

const MasterPilotAgreementModal: React.FC = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const acceptMutation = useApi.useAcceptMasterAgreement({
    onSuccess: (data) => {
      if (data.user) setUser(data.user);
      showToast('success', 'Pilot Agreement accepted.');
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      showToast('error', err.response?.data?.error || 'Failed to save agreement. Please try again.');
    },
  });

  useEffect(() => {
    const el = contentRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 50) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const canAgree = hasScrolledToBottom && !acceptMutation.isPending;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md">
      <div className="w-full max-w-3xl max-h-[90vh] sm:max-h-[92vh] flex flex-col rounded-2xl border border-[#2a3142] bg-[#12161f] shadow-2xl shadow-black/50 overflow-hidden">
        <header className="shrink-0 border-b border-[#2a3142] px-4 sm:px-6 py-4 sm:py-5 bg-[#151a24]">
          <div className="flex items-start gap-3 sm:gap-3.5">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <FiFileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/90 mb-1">
                Required agreement
              </p>
              <h2 className="text-lg sm:text-2xl font-bold text-white leading-tight tracking-tight break-words">
                TradePilot AI, Inc. Pilot (Master Trader) Agreement
              </h2>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                You must read and accept this agreement to continue as a Pilot on TradePilot.
              </p>
            </div>
          </div>
        </header>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="relative flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-3.5 break-words"
        >
          <div className="rounded-xl border border-[#2a3142] bg-[#0e1219] p-4 space-y-2">
            <p className="text-sm text-slate-300 leading-relaxed">
              This Pilot Agreement (“Agreement”) is entered into by and between TradePilot AI Inc.
              (“TradePilot,” “Company,” “we,” or “us”) and the individual accepting this Agreement below
              (“Pilot,” “you”), collectively the “Parties,” effective as of the date the Pilot accepts this
              Agreement (the “Effective Date”), as described in the Acceptance section below.
            </p>
          </div>

          {SECTIONS.map((section) => (
            <div
              key={section.title}
              className="rounded-xl border border-[#2a3142] bg-[#151a24] p-4"
            >
              <h3 className="font-bold text-white mb-2 text-[15px]">{section.title}</h3>
              {section.body ? (
                <p className="text-sm text-slate-400 leading-relaxed">{section.body}</p>
              ) : null}
              {section.subsections?.map((sub) => (
                <div key={sub.title} className={section.body ? 'mt-3' : 'mt-2 first:mt-0'}>
                  <h4 className="font-semibold text-slate-200 mb-1 text-sm">{sub.title}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{sub.body}</p>
                </div>
              ))}
            </div>
          ))}

          <div className="rounded-xl border border-primary/25 bg-primary/[0.08] p-4">
            <h3 className="font-bold text-primary mb-2 text-[15px]">Acceptance</h3>
            <p className="text-sm text-slate-200/90 leading-relaxed">
              This Agreement is presented to each Pilot electronically. By checking the box or clicking the
              button indicating acceptance of this Agreement during onboarding to the TradePilot platform,
              the Pilot acknowledges that they have read, understood, and agree to be bound by all terms of
              this Agreement, and this constitutes the Pilot’s valid electronic acceptance in place of a
              handwritten signature.
            </p>
          </div>

          {!hasScrolledToBottom && (
            <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-8 pb-2 pointer-events-none">
              <p className="text-center text-sm text-slate-400 py-2.5 rounded-lg border border-[#2a3142]/80 bg-[#12161f]/95 backdrop-blur-sm">
                Scroll to the bottom to enable &ldquo;I Agree&rdquo;
              </p>
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-[#2a3142] px-4 sm:px-6 py-3 sm:py-4 bg-[#151a24]">
          <LoadingButton
            type="button"
            onClick={() => acceptMutation.mutate()}
            disabled={!canAgree}
            isLoading={acceptMutation.isPending}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all ${
              canAgree
                ? 'bg-[#3b82f6] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/25'
                : 'bg-[#1a2030] border border-[#2a3142] text-slate-500 cursor-not-allowed'
            }`}
          >
            <FiCheck className="h-5 w-5" />
            I Agree
          </LoadingButton>
        </footer>
      </div>
    </div>
  );
};

export default MasterPilotAgreementModal;
